/**
 * 招待メール導線 (未ログイン) 用の API クライアント。
 *
 * ⚠️ ここだけ apiClient (axios) ではなく素の fetch を使う。意図的な選択なので
 *    「統一のため」で apiClient に載せ替えないこと:
 *
 *  1. 認証不要エンドポイントである。backend の
 *     `/api/invitations/verify/:token` `/api/invitations/decline/:token` は
 *     いずれもデコレータ無し (認証不要) で、Authorization ヘッダを必要としない。
 *  2. apiClient の 401 インターセプタが有害。招待リンクは「別アカウントでログイン
 *     中のブラウザ」で踏まれることがあり、apiClient 経由にすると 401/403 応答で
 *     localStorage のトークン破棄 + `sj:token` イベント発火 (= 強制ログアウト) が
 *     起きてしまう。招待の検証失敗が既存セッションを巻き込むのは望ましくない。
 *  3. 非 2xx のボディに意味がある。410 (期限切れ) や `code`
 *     (INVITATION_RESENT / ALREADY_JOINED / BELONGS_TO_OTHER_ORG /
 *     INVITATION_ALREADY_ACCEPTED) を分岐に使うため、レスポンスは成否に関わらず
 *     そのまま呼び出し側へ渡す。axios は非 2xx を reject するため素直に書けない。
 *
 * 集約しているのは backendOrigin の解決と「json を読んで {ok, status, data} を返す」
 * という定型処理のみで、呼び出し側の分岐ロジックは各ページに残している。
 */

/**
 * VITE_BACKEND_ORIGIN 未設定時は相対パスにフォールバックし、Vercel rewrite /
 * Vite proxy の /api 経由で叩く (apiClient と同じ挙動)。以前は
 * http://localhost:5001 がフォールバックで、HTTPS の本番フロントからは
 * mixed content として fetch がブロックされていた。
 */
function invitationsBaseUrl(): string {
  const backendOrigin = import.meta.env.VITE_BACKEND_ORIGIN || '';
  return `${backendOrigin}/api/invitations`;
}

/**
 * 素の fetch の結果。ok / status / data (JSON body) をそのまま返し、
 * 分岐は呼び出し側に委ねる。
 */
export interface InvitationApiResult<T = any> {
  ok: boolean;
  status: number;
  data: T;
}

async function requestInvitation<T = any>(
  path: string,
  init?: RequestInit,
): Promise<InvitationApiResult<T>> {
  const response = await fetch(`${invitationsBaseUrl()}${path}`, init);
  const data = (await response.json()) as T;
  return { ok: response.ok, status: response.status, data };
}

/** 招待トークンの検証結果 (GET /api/invitations/verify/:token) */
export interface VerifyInvitationData {
  valid?: boolean;
  email?: string;
  organization_name?: string;
  error?: string;
  code?: string;
}

/**
 * 招待トークンを検証する (認証不要)。
 *
 * ⚠️ 副作用あり: 期限切れを検出すると backend が招待を再送し token/expires_at を
 *    書き換える。辞退画面など「表示するだけ」の用途では使わず
 *    {@link previewDeclineInvitation} を使うこと。
 */
export async function verifyInvitation(
  token: string,
): Promise<InvitationApiResult<VerifyInvitationData>> {
  return requestInvitation<VerifyInvitationData>(`/verify/${token}`);
}

/** 辞退確認画面の表示用データ (GET /api/invitations/decline/:token) */
export interface DeclineInvitationPreviewData {
  organization_name?: string;
  status?: string;
  is_expired?: boolean;
  error?: string;
}

/**
 * 辞退確認画面の表示用データを取得する (認証不要・副作用なし)。
 *
 * メールスキャナのプリフェッチで踏まれても状態が変わらないことが要件のため、
 * verify ではなくこの専用エンドポイントを使う。
 */
export async function previewDeclineInvitation(
  token: string,
): Promise<InvitationApiResult<DeclineInvitationPreviewData>> {
  return requestInvitation<DeclineInvitationPreviewData>(`/decline/${token}`);
}

/** 辞退実行のレスポンス (POST /api/invitations/decline/:token) */
export interface DeclineInvitationData {
  success?: boolean;
  error?: string;
  code?: string;
}

/**
 * 招待をトークンで辞退する (認証不要)。
 * 期限切れトークンは 410 が返り状態は変更されない。
 */
export async function declineInvitationByToken(
  token: string,
): Promise<InvitationApiResult<DeclineInvitationData>> {
  return requestInvitation<DeclineInvitationData>(`/decline/${token}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
}
